import type React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert } from '@heroui/react';
import { getNodeTestIdProp } from '@/utils/nodeTestProps';

type PublishNoticeProps = {
    /** The version a publish would create. */
    nextVersion: number;
    /** The version in force, or `null` when this template has never published. */
    activeVersion: number | null;
};

/**
 * "Publishing creates v4; v3 remains on all existing entries."
 *
 * The one sentence that makes this screen safe to work in, said on the screen
 * rather than only in the publish dialog. An author deciding *whether* to change
 * a live recipe needs to know that publishing schedules a new version and
 * rewrites nothing — and they need it while they are still editing, not at the
 * moment they commit.
 *
 * `status="warning"` rather than `danger`: nothing here is wrong. It describes a
 * consequence, and the amber is the kit's colour for "this will have an effect
 * you should know about" as against "this failed".
 *
 * Title and description carry the two halves deliberately. The title is what
 * publishing *does* — it creates a new version — and the description is the part
 * people get wrong, that the version in force stays on every entry already
 * posted under it. A template that has never published has nothing to reassure
 * anyone about, so it says that instead: this will be the first version.
 */
export const PublishNotice: React.FC<PublishNoticeProps> = ({
    nextVersion,
    activeVersion,
}) => {
    const { t } = useTranslation('pages');

    return (
        <Alert
            status="warning"
            {...getNodeTestIdProp('operation-editor-publish-note')}
        >
            <Alert.Indicator />
            <Alert.Content>
                <Alert.Title>
                    {t('operations.editor.publishNoticeTitle', {
                        next: nextVersion,
                    })}
                </Alert.Title>
                <Alert.Description>
                    {activeVersion === null
                        ? t('operations.editor.publishNoticeBodyFirst')
                        : t('operations.editor.publishNoticeBody', {
                              current: activeVersion,
                          })}
                </Alert.Description>
            </Alert.Content>
        </Alert>
    );
};
